<template>
  <VSearchable
    placeholder="Search by drill data name or asset"
    :search="search"
    :loading="loading"
    :count="count"
    :auto-load="true"
    :page-count="drillData.length"
    :has-gap="false"
    @update:search="searchDrillData"
    @load-more="scroll"
  >
    <template #no-results>
      <article data-testid="drill-data-no-result" class="flex flex-col justify-center max-w-xs mx-auto text-center text-gray-700 h-76">
        <p class="text-lg">No Drill Data Found!</p>
        <p class="m-0">Please upload drill data from the data file manager to add it to your model</p>
      </article>
    </template>

    <template v-if="type === 'ai_target' && hasAiDrillData">
      <article class="flex gap-2 m-3 p-4 border border-gray-200 rounded-lg">
        <InformationCircleIcon class="flex-none w-4 h-4" />
        <div class="-mt-1">
          <p class="text-base font-medium">Heads up!</p>
          <p class="text-sm font-normal">You can only add <span class="font-semibold">one</span> AI Target to each 3D Scene.</p>
        </div>
      </article>
    </template>

    <EditorDrillDataTile
      v-for="data in drillData"
      :key="data.id"
      :data="data"
      :disabled="hasAiDrillData"
      :selected="selectedData?.id === data.id"
      :is-added-to-model="isAddedToModel(data.id)"
      @selected="selectData"
    >
      <template v-if="type === 'drill'" #access-type>
        <div class="flex-shrink-0">
          <template v-if="data.view_access_type === 'public'">
            <label
              class="text-xs leading-3 font-bold text-white rounded-full px-4 py-1 mb-0 hidden xl_block bg-copper-800"
              type="badge"
              data-testid="drill-data-access-public"
            >
              Public Info
            </label>
          </template>
          <template v-else>
            <label
              class="text-xs leading-3 font-bold text-white rounded-full px-4 py-1 mb-0 hidden xl_block bg-gold-900"
              type="badge"
              data-testid="drill-data-access-private"
            >
              Confidential
            </label>
          </template>
        </div>
      </template>
    </EditorDrillDataTile>
  </VSearchable>

  <div class="px-3 pt-1 pb-4">
    <VButton
      v-if="!loading && !drillData.length"
      data-testid="drill-data-file-manager-link"
      class="flex items-center justify-center w-full"
      component="a"
      :href="`${config.public.ADMIN_ENDPOINT}/data-files`"
      target="_blank"
      size="sm"
      variant="black"
    >
      <ArrowTopRightOnSquareIcon class="w-5 mr-2" />
      <span>Go to data file manager</span>
    </VButton>

    <VButton
      v-else
      class="flex items-center justify-center w-full"
      :disabled="!isDataSelected"
      size="sm"
      variant="black"
      @click="$emit('add-to-model')"
    >
      <PlusIcon class="w-5 mr-2 scale-125 transform" />
      <span>Add to model</span>
    </VButton>
  </div>
</template>

<script lang="ts" setup>
import type { DeepReadonly } from 'vue'
import { PlusIcon, ArrowTopRightOnSquareIcon, InformationCircleIcon } from '@heroicons/vue/24/outline'

import { isClusterLayer } from '@/lib/three-d-editor/utils'
import type { Drill } from '@/entities/drill'
import type { QueryParameters } from '@/composables/useIndexSearch'
import type { EditDeck } from '~/composables/useEditDeck'
import type { UseThreeDViewer } from '~/composables/useThreeDViewer'
import { debounce } from '@/lib/utils'

interface SortQuery extends QueryParameters {
  sortKey: 'name' | 'updated_at'
  sortDir: '-' | ''
}
interface ApiQuery {
  query: string | null
  limit: number
}

const props = withDefaults(
  defineProps<{
    deck: DeepReadonly<EditDeck>
    threeDViewer: UseThreeDViewer
    type: 'drill' | 'ai_target'
  }>(),
  {
    type: 'drill',
  },
)
const emit = defineEmits<{
  (e: 'selected', data: Drill): void
  (e: 'add-to-model'): void
}>()

const isDataSelected = ref(false)
const selectedData = ref<Drill | null>(null)
const config = useRuntimeConfig()
const {
  results: data,
  query: search,
  count,
  continuation,
  loading,
} = useIndexSearch<Drill, SortQuery, ApiQuery>({
  initParams: { limit: 12, sortDir: '-', sortKey: 'updated_at' },
  endpoint: () => `v2/drill/companies/${props.deck.company_id}/data`,
  finalParameters: ({ query, limit, sortKey, sortDir }) => ({
    sort_by: `${sortDir}${sortKey}`,
    query,
    limit,
  }),
})

const hasAiDrillData = computed(() => {
  if (props.type !== 'ai_target') return false
  return props.threeDViewer.layerGroups.value.some(layerGroup => layerGroup.type === 'drill' && layerGroup.layers.some(isClusterLayer))
})

const drillData = computed(() => {
  const out = data.value.filter((data: Drill) => data.data_type === props.type)

  if (props.deck.view_access_type === 'public') {
    return out.filter((data: Drill) => data.view_access_type === 'public')
  }

  return out
})

const searchDrillData = debounce(_searchDrillData, 500)

function _searchDrillData(query: string) {
  search.value = query
}

function selectData(data: Drill) {
  selectedData.value = data
  isDataSelected.value = true
  emit('selected', data)
}

async function scroll() {
  if (!loading.value && count.value > data.value.length) {
    continuation.value++
  }
}

function isAddedToModel(id: number) {
  return props.threeDViewer.layerGroups.value.some(
    layerGroup => layerGroup.type === 'drill' && (layerGroup.dataSetId === id || layerGroup.layers.some(l => l.dataSetId === id)),
  )
}
</script>
